import { createClient } from '@supabase/supabase-js';

// Define Types
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export interface Database {
  public: {
    Tables: {
      notification_recipients: {
        Row: {
          created_at: string
          id: string
          notification_id: string
          recipient_name: string
          recipient_type: string
        }
        Insert: {
          created_at?: string
          id?: string
          notification_id: string
          recipient_name: string
          recipient_type: string
        }
        Update: {
          created_at?: string
          id?: string
          notification_id?: string
          recipient_name?: string
          recipient_type?: string
        }
      }
      notifications: {
        Row: {
          archived: boolean
          created_at: string
          exact_order_number: string | null
          id: string
          message: string
          priority: string
          status: string
          type: string
          updated_at: string
          user_id: string
          user_name: string
          vo_number: string | null
          is_deleted: boolean
          deleted_at: string | null
        }
        Insert: {
          archived?: boolean
          created_at?: string
          exact_order_number?: string | null
          id?: string
          message: string
          priority: string
          status: string
          type: string
          updated_at?: string
          user_id: string
          user_name: string
          vo_number?: string | null
          is_deleted?: boolean
          deleted_at?: string | null
        }
        Update: {
          archived?: boolean
          created_at?: string
          exact_order_number?: string | null
          id?: string
          message?: string
          priority?: string
          status?: string
          type?: string
          updated_at?: string
          user_id?: string
          user_name?: string
          vo_number?: string | null
          is_deleted?: boolean
          deleted_at?: string | null
        }
      }
      profiles: {
        Row: {
          avatar_url: string | null
          full_name: string | null
          id: string
          updated_at: string | null
          username: string | null
          website: string | null
        }
        Insert: {
          avatar_url?: string | null
          full_name?: string | null
          id: string
          updated_at?: string | null
          username?: string | null
          website?: string | null
        }
        Update: {
          avatar_url?: string | null
          full_name?: string | null
          id?: string
          updated_at?: string | null
          username?: string | null
          website?: string | null
        }
      }
      medewerkers: {
        Row: {
          id: string
          naam: string
          functie: string | null
          afdeling: string | null
          email: string | null
          username: string
          password: string
          created_at: string | null
          updated_at: string | null
          active: boolean | null
        }
        Insert: {
          id?: string
          naam: string
          functie?: string | null
          afdeling?: string | null
          email?: string | null
          username: string
          password: string
          created_at?: string | null
          updated_at?: string | null
          active?: boolean | null
        }
        Update: {
          id?: string
          naam?: string
          functie?: string | null
          afdeling?: string | null
          email?: string | null
          username?: string
          password?: string
          created_at?: string | null
          updated_at?: string | null
          active?: boolean | null
        }
      }
      inpakkers: {
        Row: {
          id: string
          naam: string
          actief: boolean | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          naam: string
          actief?: boolean | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          naam?: string
          actief?: boolean | null
          created_at?: string
          updated_at?: string
        }
      }
      notification_edit_history: {
        Row: {
          id: string
          notification_id: string
          editor_id: string
          editor_name: string
          edit_date: string
          previous_message: string | null
          new_message: string | null
          field_name: string | null
          previous_value: string | null
          new_value: string | null
          change_type: string | null
        }
        Insert: {
          id?: string
          notification_id: string
          editor_id: string
          editor_name: string
          edit_date?: string
          previous_message?: string | null
          new_message?: string | null
          field_name?: string | null
          previous_value?: string | null
          new_value?: string | null
          change_type?: string | null
        }
        Update: {
          id?: string
          notification_id?: string
          editor_id?: string
          editor_name?: string
          edit_date?: string
          previous_message?: string | null
          new_message?: string | null
          field_name?: string | null
          previous_value?: string | null
          new_value?: string | null
          change_type?: string | null
        }
      }
      order_notes: {
        Row: {
          id: string
          reference_id: string
          reference_type: string
          text: string
          author_id: string
          author_name: string
          timestamp: string
        }
        Insert: {
          id?: string
          reference_id: string
          reference_type: string
          text: string
          author_id: string
          author_name: string
          timestamp?: string
        }
        Update: {
          id?: string
          reference_id?: string
          reference_type?: string
          text?: string
          author_id?: string
          author_name?: string
          timestamp?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

// Define convenient type aliases for Tables
export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type TablesInsert<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert'];
export type TablesUpdate<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Update'];

// Export specific types for all tables
export type NotificationsResponse = Tables<'notifications'>;
export type NotificationRecipientsResponse = Tables<'notification_recipients'>;
export type MedewerkerResponse = Tables<'medewerkers'> & {
  last_logout?: string | null;
  last_login?: string | null;
};
export type MedewerkerInsert = TablesInsert<'medewerkers'>;
export type MedewerkerUpdate = TablesUpdate<'medewerkers'>;
export type InpakkerResponse = Tables<'inpakkers'>;
export type NotificationEditHistoryResponse = Tables<'notification_edit_history'>;
export type OrderNoteResponse = Tables<'order_notes'>;
export type OrderNoteInsert = TablesInsert<'order_notes'>;
export type OrderNoteUpdate = TablesUpdate<'order_notes'>;

// Supabase client with fallback for development environments
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://pamlmqhftnqqpqpxlsbb.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBhbWxtcWhmdG5xcXBxcHhsc2JiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM4Mzc5MDgsImV4cCI6MjA1OTQxMzkwOH0.PhUjQxhquYFwClAs4BHtEof3IPUVrDN9WWFxIUt6EKM';

// Log initialization parameters for debugging
console.log('Initializing Supabase client with:', { 
  url: supabaseUrl, 
  keyLength: supabaseAnonKey ? supabaseAnonKey.length : 0 
});

// Supabase client with realtime enabled
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Function to create and setup channel for notifications
export const setupNotificationsChannel = () => {
  console.log('Setting up dedicated channel for notifications');
  
  // Create a dedicated channel for notifications
  return supabase
    .channel('notifications-changes')
    .on('postgres_changes', 
      {
        event: '*',
        schema: 'public',
        table: 'notifications'
      }, 
      (payload) => {
        console.log('Notification change detected via dedicated channel:', payload);
      }
    )
    .subscribe((status) => {
      console.log('Notifications channel subscription status:', status);
    });
};

// Initialize the notifications channel
setupNotificationsChannel();
